import type { RescueDocument, RescueDocumentType, RescueRequiredDocument } from './documentQueries';
import { US_STATE_OPTIONS } from './utils';

/** A word joiner, so 501(c)(3) never breaks across lines between its two parens. */
export const C3 = '501(c)⁠(3)';

/** Spelled out: a bare code reads as a word for OR, IN, ME and HI. */
export function stateName(state: string): string {
  return US_STATE_OPTIONS.find((option) => option.abbreviation === state)?.name ?? state;
}

/** How a state's certificate is named mid-sentence, e.g. "Ohio exemption certificate". */
export function certificateName(state: string | null): string {
  return state ? `${stateName(state)} exemption certificate` : 'exemption certificate';
}

/** What the federal document is called, which for a government shelter is not a 501(c)(3) letter. */
export function letterName(government: boolean): string {
  return government ? 'Proof of government status' : `${C3} determination letter`;
}

/** Whole days from today until a date-only expiry. Negative once it has passed. */
export function daysUntil(dateOnly: string): number {
  const [year, month, day] = dateOnly.slice(0, 10).split('-').map(Number);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((Date.UTC(year, month - 1, day) - today) / (24 * 60 * 60 * 1000));
}

/** Approved, unexpired, and inside the window where the prototype brings Replace back. */
export function expiringSoon(doc: RescueDocument): boolean {
  if (doc.status !== 'APPROVED' || doc.expired || doc.neverExpires || !doc.expirationDate) {
    return false;
  }
  return daysUntil(doc.expirationDate) <= 30;
}

/** Filed within the hour, so "just now" is honest. */
export function uploadedJustNow(doc: RescueDocument): boolean {
  const uploaded = new Date(doc.uploadedAt).getTime();
  return !Number.isNaN(uploaded) && Date.now() - uploaded < 60 * 60 * 1000;
}

export function certificateFormName(entry: RescueRequiredDocument): string {
  return entry.bucket === 'MULTISTATE_F0003' ? 'Form F0003' : 'State certificate';
}

/** An SST state names the form it takes; everywhere else the certificate varies, so it is described. */
export function documentLabel(entry: RescueRequiredDocument, government: boolean): string {
  if (entry.documentType === 'NONPROFIT_LETTER') {
    return letterName(government);
  }
  if (entry.bucket === 'MULTISTATE_F0003') {
    return 'Certificate of Exemption (Form F0003)';
  }
  return entry.state ? `${stateName(entry.state)} sales tax exemption certificate` : 'Sales tax exemption certificate';
}

/** The documents filed against a state, as against the federal letter. */
export function isStateDocument(type: RescueDocumentType): boolean {
  return type !== 'NONPROFIT_LETTER';
}

/** What each state document is called as a label. Lowercase it mid-sentence: "your proof of ineligibility". */
export function stateDocumentKind(type: RescueDocumentType): string {
  switch (type) {
    case 'STATE_EXEMPTION_APPLICATION':
      return 'Proof of application';
    case 'STATE_EXEMPTION_INELIGIBLE':
      return 'Proof of ineligibility';
    default:
      return 'Exemption certificate';
  }
}

/** The hint under a state document's uploader, for the two that aren't the certificate itself. */
export function stateDocumentHint(type: RescueDocumentType, state: string | null): string | null {
  const where = state ? stateName(state) : 'your state';
  switch (type) {
    case 'STATE_EXEMPTION_APPLICATION':
      return `Your application to ${where} for a sales tax exemption, or the state's confirmation that it was received. Once we approve it, your Fi Nano orders can ship while ${where} decides.`;
    case 'STATE_EXEMPTION_INELIGIBLE':
      return `The letter from ${where} denying your exemption, or showing your organization doesn't qualify. Once we approve it, your Fi Nano orders can ship.`;
    default:
      return null;
  }
}

/** An upload box's hint with the file limit, which goes on its own line under a longer application or denial hint. */
export function withFileLimit(hint: string, type: RescueDocumentType): string {
  const ownLine = type === 'STATE_EXEMPTION_APPLICATION' || type === 'STATE_EXEMPTION_INELIGIBLE';
  return `${hint}${ownLine ? '\n' : ' '}PDF up to 20MB.`;
}
